import { workStatusCardSchema } from '@/schemas/contracts/cards'
import { taskInRun } from './taskRuns'

const ERROR_STATUSES = new Set(['failed'])
const WAITING_STATUSES = new Set(['waiting'])
const DONE_STATUSES = new Set(['completed', 'skipped'])
// Server-owned settlement reason. A deferred task is `pending` — it is not
// running — so "Work in progress" would claim something the server is not doing.
const DEFERRED = 'deferred_to_later_turn'

export function taskTimelineStatus(status) {
  if (DONE_STATUSES.has(status)) return 'done'
  if (ERROR_STATUSES.has(status)) return 'error'
  if (WAITING_STATUSES.has(status)) return 'waiting'
  if (status === 'pending') return 'pending'
  return 'running'
}

/**
 * Parses the complete, server-owned checkpoint projection.  Its `activity` is
 * merged into the one timeline the browser renders (see `timelineRows`); the
 * tasks it carries are that timeline's groups.
 */
export function toWorkStatusSnapshot(payload) {
  const parsed = workStatusCardSchema.safeParse(payload)
  if (!parsed.success || !parsed.data.tasks.length) return null
  return parsed.data
}

export function workStatusSummary(snapshot, runId = null) {
  const tasks = (snapshot?.tasks ?? []).filter((task) => taskInRun(runId, task))
  if (!tasks.length) return null
  const active = tasks.find((task) => task.status === 'in_progress')
  if (active?.detail) return active.detail
  if (active?.title) return active.title
  const waiting = tasks.find((task) => task.status === 'waiting')
  if (waiting?.detail) return waiting.detail
  if (waiting) return 'Waiting for approval or input'
  if (tasks.some((task) => task.status === 'failed')) return 'Work needs attention'
  // An agent-authored list finishing is the agent saying so, and the collapsed
  // header is exactly where that distinction is easiest to lose.
  if (tasks.every((task) => DONE_STATUSES.has(task.status))
    && tasks.some((task) => task.client_key)) {
    return 'Steps reported complete'
  }
  const deferred = tasks.filter((task) => task.settle_reason === DEFERRED).length
  if (deferred) {
    return `${deferred} step${deferred === 1 ? '' : 's'} left — continues on your next message`
  }
  if (tasks.some((task) => !DONE_STATUSES.has(task.status))) return 'Work in progress'
  return 'Work completed'
}

export function workStatusIsComplete(snapshot) {
  return Boolean(snapshot?.tasks?.length) && snapshot.tasks.every((task) => DONE_STATUSES.has(task.status))
}
